/**
 * 侧边栏组件
 * 提供应用的侧边导航和快捷功能
 */

import React, { useState } from 'react';
import { useAppViewModel } from '../../viewmodels/appviewmodel.jsx';
import { useTheme } from '../../design-system/hooks/useTheme.jsx';

/**
 * 侧边栏组件
 * @param {Object} props - 组件属性
 * @param {boolean} props.isOpen - 是否打开侧边栏
 * @param {Function} props.onClose - 关闭侧边栏回调
 * @param {string} props.className - 自定义样式类
 */
function Sidebar({ isOpen = false, onClose, className = '' }) {
  const { globalState } = useAppViewModel();
  const { currentTheme, toggleTheme } = useTheme();
  const [activeSection, setActiveSection] = useState('navigation');

  // 侧边栏菜单项
  const menuItems = [
    {
      id: 'navigation',
      label: '导航',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      ),
      items: [
        { label: '首页', href: '#home' },
        { label: '关于', href: '#about' },
        { label: '项目', href: '#projects' },
        { label: '联系', href: '#contact' }
      ]
    },
    {
      id: 'tools',
      label: '工具',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      items: [
        { label: '主题切换', action: toggleTheme },
        { label: '设计系统', href: '#design-system' },
        { label: '组件预览', href: '#components' }
      ]
    },
    {
      id: 'info',
      label: '信息',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      items: [
        { label: 'MVVM 架构', href: '#architecture' },
        { label: '技术栈', href: '#tech-stack' },
        { label: '版本信息', href: '#version' }
      ]
    }
  ];

  /**
   * 处理菜单项点击
   * @param {Object} item - 菜单项
   */
  const handleItemClick = (item) => {
    if (item.action) {
      item.action();
    } else if (item.href) {
      // 这里可以添加路由导航逻辑
      console.log('Navigate to:', item.href);
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* 遮罩层 */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* 侧边栏 */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:relative lg:translate-x-0
        ${className}
      `}>
        {/* 侧边栏头部 */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
              <svg className="w-4 h-4 text-primary-foreground" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              </svg>
            </div>
            <span className="font-semibold text-foreground">菜单</span>
          </div>
          
          {/* 关闭按钮 (仅移动端显示) */}
          <button 
            onClick={onClose}
            className="lg:hidden p-1 rounded hover:bg-accent"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 侧边栏内容 */}
        <div className="flex-1 overflow-y-auto p-4">
          <nav className="space-y-6">
            {menuItems.map((section) => (
              <div key={section.id}>
                {/* 分组标题 */}
                <button
                  onClick={() => setActiveSection(
                    activeSection === section.id ? null : section.id
                  )}
                  className="flex items-center justify-between w-full p-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    {section.icon}
                    <span>{section.label}</span>
                  </div>
                  <svg 
                    className={`w-4 h-4 transition-transform ${
                      activeSection === section.id ? 'rotate-90' : ''
                    }`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {/* 分组项目 */}
                {activeSection === section.id && (
                  <div className="mt-2 ml-6 space-y-1">
                    {section.items.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => handleItemClick(item)}
                        className="block w-full text-left p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* 侧边栏底部 */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>当前主题</span>
            <span className="capitalize">{currentTheme?.name || 'light'}</span>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
            <span>应用状态</span>
            <span className={`capitalize ${
              globalState?.isInitialized ? 'text-green-500' : 'text-yellow-500'
            }`}>
              {globalState?.isInitialized ? '已就绪' : '初始化中'}
            </span>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;